import {Archive, ExpandMore, Sync} from '@mui/icons-material';
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {useCallback, useEffect, useState} from 'react';
import {UserAccount} from '../../../__generated__/resolvers-types';
import {env} from '../../../constants';
import {firebaseFirestore} from '../../../setup/firebase';

export interface ContactMessagesProps {
  user: UserAccount | null | undefined;
}

interface ContactMessageDoc {
  id: string;
  name: string;
  email: string;
  category: string;
  message: string;
  status?: string;
  createdTimestamp: Timestamp;
  attachments: Array<{data: string; name: string; type: string}>;
}

export default function ContactMessages({user}: ContactMessagesProps) {
  const [messages, setMessages] = useState<ContactMessageDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(
        collection(firebaseFirestore, env.collection.contactMessages),
        where('status', '==', 'active'),
        orderBy('createdTimestamp', 'desc'),
        limit(10),
      );
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map((d) => ({id: d.id, ...d.data()}) as ContactMessageDoc);
      setMessages(docs);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleArchive = useCallback(async (messageId: string) => {
    try {
      const docRef = doc(firebaseFirestore, env.collection.contactMessages, messageId);
      await updateDoc(docRef, {status: 'archived'});
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
    } catch (error) {
      console.error('Error archiving message:', error);
    }
  }, []);

  const handleMigration = useCallback(async () => {
    setMigrating(true);
    try {
      const q = query(collection(firebaseFirestore, env.collection.contactMessages));
      const snapshot = await getDocs(q);

      const docsToUpdate = snapshot.docs.filter((d) => {
        const data = d.data();
        return !data.status;
      });

      if (docsToUpdate.length === 0) {
        alert('No documents need migration.');
        return;
      }

      const batch = writeBatch(firebaseFirestore);
      docsToUpdate.forEach((d) => {
        batch.update(d.ref, {status: 'active'});
      });
      await batch.commit();

      alert(`Migration complete. Updated ${docsToUpdate.length} documents.`);
      await fetchMessages();
    } catch (error) {
      console.error('Error running migration:', error);
      alert('Migration failed. Check console for details.');
    } finally {
      setMigrating(false);
    }
  }, [fetchMessages]);

  const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getTimeIndicator = (timestamp: Timestamp) => {
    const createdAt = timestamp.toDate();
    const now = dayjs();
    const diffHours = now.diff(dayjs(createdAt), 'hour');
    const diffDays = now.diff(dayjs(createdAt), 'day');

    if (diffHours < 24) {
      return <Chip label="24h" color="error" size="small" sx={{ml: 1}} />;
    }
    if (diffDays <= 7) {
      return <Chip label="7d" color="warning" size="small" sx={{ml: 1}} />;
    }
    return null;
  };

  if (!user?.permissions?.includes('owner')) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Contact Messages
        </Typography>
        <Typography color="text.secondary">You do not have permission to view this page.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Contact Messages</Typography>
        <Button
          variant="outlined"
          startIcon={migrating ? <CircularProgress size={16} /> : <Sync />}
          onClick={handleMigration}
          disabled={migrating}
        >
          Run Migration
        </Button>
      </Stack>
      <Typography variant="body1" color="text.secondary" paragraph>
        Showing latest 10 active contact messages. Archive messages to remove them from this list.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : messages.length === 0 ? (
        <Typography color="text.secondary" align="center" py={4}>
          No active contact messages.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {messages.map((message) => (
            <Accordion key={message.id} expanded={expanded === message.id} onChange={handleAccordionChange(message.id)}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{width: '100%', pr: 2}}>
                  <Typography sx={{flexShrink: 0, fontWeight: 'medium'}}>{message.name}</Typography>
                  <Typography color="text.secondary" sx={{flexShrink: 0}}>
                    {message.email}
                  </Typography>
                  <Chip label={message.category} size="small" variant="outlined" />
                  <Box sx={{flexGrow: 1}} />
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(message.createdTimestamp.toDate()).format('YYYY-MM-DD HH:mm')}
                  </Typography>
                  {getTimeIndicator(message.createdTimestamp)}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{whiteSpace: 'pre-wrap'}}>
                  {message.message}
                </Typography>
                {message.attachments.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{mt: 2, display: 'block'}}>
                    {message.attachments.length} attachment(s)
                  </Typography>
                )}
              </AccordionDetails>
              <AccordionActions>
                <Button startIcon={<Archive />} color="warning" onClick={() => handleArchive(message.id)}>
                  Archive
                </Button>
              </AccordionActions>
            </Accordion>
          ))}
        </Stack>
      )}
    </Box>
  );
}
